import { Activity, Droplet, Repeat } from "lucide-react";

/**
 * Where pull beats push. Three use cases, each paired with the specific
 * push-oracle failure mode it dodges. Static cards; the design is the
 * argument, not the motion.
 */
export function UseCases() {
  return (
    <section
      className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-24 pb-12 sm:px-10 sm:pt-32"
      aria-labelledby="use-title"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-m45">
        <span className="text-emerald">●</span> USE CASES
      </p>
      <h2
        id="use-title"
        className="mt-6 font-serif text-3xl leading-[1.05] tracking-[-0.012em] text-text sm:text-5xl md:text-6xl"
      >
        Where <em className="italic text-emerald">milliseconds</em> matter.
      </h2>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-m70">
        Built for protocols where a stale price means a bad fill, a missed
        liquidation, or toxic flow leaking into the AMM. The freshness gap
        is where margin is lost.
      </p>

      <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3">
        <Case
          Icon={Activity}
          title="Perpetual DEX execution"
          body="Open and close positions against a price that can't be a block stale. Matters most under volatility — when the gap between push updates is when traders front-run the venue."
          failure="Push oracle problem: between refreshes, the mark price diverges from the spot; traders pick off the stale reference."
        />
        <Case
          Icon={Droplet}
          title="Lending liquidations"
          body="Liquidate without racing the next ledger close. The liquidator's call and the price update arrive in the same transaction — atomic, fair, no mempool race."
          failure="Push oracle problem: liquidator and the next oracle refresh fight for inclusion; collateral price drifts mid-race."
        />
        <Case
          Icon={Repeat}
          title="Oracle-priced AMM swaps"
          body="Quote against a price that matches the spot market mid. Reduces the LP's exposure to toxic flow arbing the AMM's stale reference."
          failure="Push oracle problem: the AMM's reference lags behind spot for the full refresh interval; arb sweeps the pool."
        />
      </div>
    </section>
  );
}

function Case({
  Icon,
  title,
  body,
  failure,
}: {
  Icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  title: string;
  body: string;
  failure: string;
}) {
  return (
    <article className="flex flex-col gap-4 rounded-xl border border-border bg-surface/40 p-6 transition-colors hover:border-border-2 hover:bg-surface/60">
      <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-bg-deep/60">
        <Icon className="size-5 text-emerald" strokeWidth={1.5} />
      </div>
      <h3 className="font-serif text-[22px] leading-tight text-text">{title}</h3>
      <p className="text-[14px] leading-relaxed text-m70">{body}</p>
      <div className="mt-auto border-t border-border pt-3">
        <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-amber">
          What pull dodges
        </div>
        <p className="mt-1.5 text-[12.5px] leading-relaxed text-m55">{failure}</p>
      </div>
    </article>
  );
}
